import type { CarSpec } from './types';

/** Berlina de referencia: el coche con el que se aprende lo básico. */
export const SEDAN: CarSpec = {
  id: 'sedan',
  class: 'sedan',
  nameKey: 'car.sedan.name',
  massKg: 1420,
  maxPowerKw: 110,
  maxTorqueNm: 250,
  brakeForceN: 16500,
  tireGrip: 1.0,
  centerOfGravityHeightM: 0.55,
  dims: {
    lengthM: 4.6,
    widthM: 1.82,
    heightM: 1.45,
    wheelbaseM: 2.72,
    trackM: 1.56,
    wheelRadiusM: 0.31,
  },
  dragCoef: 0.42,
  rollingResist: 0.013,
  creepForceN: 900,
  gearRatios: [3.6, 2.19, 1.41, 1.0, 0.78, 0.64],
  finalDrive: 3.35,
  assists: { abs: true, tc: true, esc: true, reverseCam: false, hillHold: true },
  color: '#3b82c4',
};

/** Utilitario urbano: ágil, corto, ideal para maniobra y aparcamiento. */
export const CITY: CarSpec = {
  id: 'city',
  class: 'city',
  nameKey: 'car.city.name',
  massKg: 1080,
  maxPowerKw: 74,
  maxTorqueNm: 160,
  brakeForceN: 13000,
  tireGrip: 0.98,
  centerOfGravityHeightM: 0.52,
  dims: {
    lengthM: 3.9,
    widthM: 1.74,
    heightM: 1.48,
    wheelbaseM: 2.48,
    trackM: 1.5,
    wheelRadiusM: 0.29,
  },
  dragCoef: 0.45,
  rollingResist: 0.013,
  creepForceN: 700,
  gearRatios: [3.8, 2.2, 1.45, 1.03, 0.8],
  finalDrive: 3.6,
  assists: { abs: true, tc: true, esc: true, reverseCam: false, hillHold: true },
  color: '#d9534f',
};

/** Eléctrico: par instantáneo, regenerativa y conducción de un pedal. */
export const EV: CarSpec = {
  id: 'ev',
  class: 'ev',
  nameKey: 'car.ev.name',
  massKg: 1750,
  maxPowerKw: 150,
  maxTorqueNm: 310,
  brakeForceN: 18000,
  tireGrip: 1.0,
  centerOfGravityHeightM: 0.48, // baterías en el suelo
  dims: {
    lengthM: 4.4,
    widthM: 1.85,
    heightM: 1.55,
    wheelbaseM: 2.75,
    trackM: 1.58,
    wheelRadiusM: 0.32,
  },
  dragCoef: 0.36,
  rollingResist: 0.012,
  creepForceN: 650,
  gearRatios: [8.2], // reductora única
  finalDrive: 1.0,
  regenBraking: true,
  assists: { abs: true, tc: true, esc: true, reverseCam: true, hillHold: true },
  color: '#4fae62',
};

// ---------------------------------------------------------------------------
// Coches de cambio MANUAL: cada uno entrena una técnica distinta del embrague.
// ---------------------------------------------------------------------------

/** Utilitario manual de autoescuela: embrague dócil y zona de fricción ancha. */
export const M_CITY: CarSpec = {
  id: 'm-city',
  class: 'city',
  nameKey: 'car.mcity.name',
  massKg: 1100,
  maxPowerKw: 66,
  maxTorqueNm: 145,
  brakeForceN: 13000,
  tireGrip: 0.98,
  centerOfGravityHeightM: 0.52,
  dims: {
    lengthM: 3.95,
    widthM: 1.75,
    heightM: 1.47,
    wheelbaseM: 2.5,
    trackM: 1.5,
    wheelRadiusM: 0.29,
  },
  dragCoef: 0.44,
  rollingResist: 0.013,
  creepForceN: 0,
  gearRatios: [3.9, 2.15, 1.4, 1.03, 0.82],
  finalDrive: 3.9,
  assists: { abs: true, tc: false, esc: false, reverseCam: false, hillHold: false },
  color: '#e8a13c',
  manual: {
    idleRpm: 850,
    stallRpm: 580,
    redlineRpm: 6300,
    torqueCurve: [
      [850, 0.52],
      [1500, 0.68],
      [2500, 0.86],
      [3800, 1.0],
      [5200, 0.94],
      [6300, 0.72],
    ],
    clutch: { bitePoint: 0.5, biteWidth: 0.24, maxTorqueNm: 300 },
    reverseRatio: 3.7,
    engineInertia: 0.16,
  },
};

/** Furgoneta diésel: par enorme abajo, difícil de calar, cambios tempranos. */
export const M_DIESEL: CarSpec = {
  id: 'm-diesel',
  class: 'van',
  nameKey: 'car.mdiesel.name',
  massKg: 1920,
  maxPowerKw: 90,
  maxTorqueNm: 320,
  brakeForceN: 19000,
  tireGrip: 0.96,
  centerOfGravityHeightM: 0.72,
  dims: {
    lengthM: 5.0,
    widthM: 1.96,
    heightM: 1.95,
    wheelbaseM: 3.1,
    trackM: 1.66,
    wheelRadiusM: 0.34,
  },
  dragCoef: 0.62,
  rollingResist: 0.014,
  creepForceN: 0,
  gearRatios: [4.2, 2.3, 1.5, 1.05, 0.8],
  finalDrive: 3.5,
  assists: { abs: true, tc: false, esc: false, reverseCam: true, hillHold: false },
  color: '#8f9aa6',
  manual: {
    idleRpm: 800,
    stallRpm: 500,
    redlineRpm: 4600,
    torqueCurve: [
      [800, 0.6],
      [1300, 0.85],
      [1750, 1.0],
      [2800, 0.97],
      [3800, 0.8],
      [4600, 0.55],
    ],
    clutch: { bitePoint: 0.52, biteWidth: 0.2, maxTorqueNm: 560 },
    reverseRatio: 4.0,
    engineInertia: 0.3,
  },
};

/** Coupé deportivo: embrague seco y cortante, 6 marchas, pide rev-matching. */
export const M_SPORT: CarSpec = {
  id: 'm-sport',
  class: 'sedan',
  nameKey: 'car.msport.name',
  massKg: 1290,
  maxPowerKw: 147,
  maxTorqueNm: 240,
  brakeForceN: 19500,
  tireGrip: 1.08,
  centerOfGravityHeightM: 0.46,
  dims: {
    lengthM: 4.4,
    widthM: 1.83,
    heightM: 1.32,
    wheelbaseM: 2.6,
    trackM: 1.56,
    wheelRadiusM: 0.32,
  },
  dragCoef: 0.36,
  rollingResist: 0.012,
  creepForceN: 0,
  gearRatios: [3.6, 2.35, 1.75, 1.35, 1.09, 0.9],
  finalDrive: 3.9,
  assists: { abs: true, tc: false, esc: false, reverseCam: false, hillHold: false },
  color: '#c23b4e',
  manual: {
    idleRpm: 950,
    stallRpm: 650,
    redlineRpm: 7600,
    torqueCurve: [
      [950, 0.45],
      [2000, 0.62],
      [3500, 0.8],
      [5500, 1.0],
      [6800, 0.95],
      [7600, 0.8],
    ],
    clutch: { bitePoint: 0.45, biteWidth: 0.12, maxTorqueNm: 430 },
    reverseRatio: 3.4,
    engineInertia: 0.11,
    drivenAxle: 'rear',
  },
};

export const CARS: Record<string, CarSpec> = {
  [SEDAN.id]: SEDAN,
  [CITY.id]: CITY,
  [EV.id]: EV,
  [M_CITY.id]: M_CITY,
  [M_DIESEL.id]: M_DIESEL,
  [M_SPORT.id]: M_SPORT,
};

/** Coches de un tipo de transmisión concreto (para selectores de la UI). */
export function carsByTransmission(t: 'auto' | 'manual'): CarSpec[] {
  return Object.values(CARS).filter((c) => (c.manual ? 'manual' : 'auto') === t);
}

export function getCar(id: string): CarSpec {
  const spec = CARS[id];
  if (!spec) throw new Error(`CarSpec desconocido: ${id}`);
  return spec;
}
